import React, { useState, useEffect, Fragment, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Divider,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  TablePagination,
  Card,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import CloseIcon from '@mui/icons-material/Close';
import RepeatIcon from '@mui/icons-material/Repeat';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import HistoryIcon from '@mui/icons-material/History';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import IconButton from '@mui/material/IconButton';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { hostApiClient } from '../../hostApiClient';
import { notifyRoomBookingNeedsActionCountChanged } from '../../utils/roomBookingNeedsActionCount';
import { HostRoomBooking, AdditionalGear } from '../../models/HostRoomBooking';
import { PaymentStatus, RoomBookingStatus, StripeRefundStatus } from '../../models/RoomBooking';
import { PaymentMethod } from '../../models/Organization';
import { zonedTimeToUtc } from 'date-fns-tz';
import { BookingSlotsConfig, Room, RoomSummary } from '../../models/Room';
import { apiClient } from '../../publicApiClient';
import TimeAndDateStep from '../../components/booking-steps/TimeAndDateStep';
import { ProposeChangeRequest, RoomBookingChangeStatus, CancelChangeRequest } from '../../models/RoomBookingChange';
import { BookingPasscodeStatus } from '../../models/SmartLock';
import RoomBookingUserAvatar from '../../components/RoomBookingUserAvatar';
import BookingCreatedAtLabel from '../../components/BookingCreatedAtLabel';
import BookingRejectionDialog from '../../components/BookingRejectionDialog';
import PassBookingChip from '../../components/passes/PassBookingChip';
import { roomBookingDetailsPath } from '../../utils/roomBookingDetailsPath';
import { hostRequestsQueryBookingTarget } from '../../utils/hostRequestsBookingDeepLink';
import UserBandAffiliationLine from '../../components/UserBandAffiliationLine';
import { canCollectRoomBookingPayment } from '../../utils/bookingPaymentCollection';
import { groupHostRoomBookingRequests, hostSeriesCardOwnsRoomActions } from '../../utils/hostRoomBookingGrouping';
import {
  bookingIdsMentionedInError,
  declineTargetsUseSeriesEndpoint,
  pendingOnlineRoomUsesSeriesEndpoint,
  planSeriesAccept,
} from '../../utils/seriesAcceptPlan';
import HostRecurringPartialAcceptDialog from '../../components/HostRecurringPartialAcceptDialog';

type BookingActionType = 'accept' | 'decline' | 'propose' | 'cancelChange' | 'cancel' | 'refund' | 'collect';
type SeriesProcessingAction = 'acceptAll' | 'declineAll';

type HostNegativeDialogMode = 'decline' | 'cancel';

type ProcessingState =
  | { bookingId: string; action: BookingActionType }
  | { seriesId: string; action: SeriesProcessingAction };

function resolveHostRoomBookingBandName(
  booking: Pick<HostRoomBooking, 'bandName' | 'participantDetails'>
): string | null {
  const name = booking.participantDetails?.bandName || booking.bandName;
  const trimmed = name?.trim();
  return trimmed || null;
}

// Status chip colors
const statusColors = {
  PENDING_CONFIRMATION: 'warning',
  CONFIRMED_BY_HOST: 'success',
  AUTO_CONFIRMED: 'success',
  AUTO_CONFIRMED_PENDING_PAYMENT: 'success',
  CANCELLED_BY_USER: 'default',
  CANCELLED_BY_HOST: 'error',
  PENDING_PAYMENT: 'error',
  CANCELLED_DUE_AMOUNT_BLOCK_EXPIRATION: 'error',
  CANCELLED_DUE_NO_RESPONSE_FROM_HOST: 'error',
} as const;

/** Sessions the host (or system) has accepted — vs pending / cancelled / etc. */
const HOST_CONFIRMED_STATUSES: ReadonlySet<RoomBookingStatus> = new Set([
  RoomBookingStatus.CONFIRMED_BY_HOST,
  RoomBookingStatus.AUTO_CONFIRMED,
  RoomBookingStatus.AUTO_CONFIRMED_PENDING_PAYMENT,
]);
const REFUND_BLOCKED_STATUSES: ReadonlySet<StripeRefundStatus> = new Set([
  StripeRefundStatus.SUCCEEDED,
  StripeRefundStatus.PENDING,
]);
/** Set to true when host manual refund UI is ready to ship. API client remains wired either way. */
const ROOM_BOOKING_REFUND_UI_ENABLED = false;
const CANCELLED_BOOKING_STATUSES: ReadonlySet<RoomBookingStatus> = new Set([
  RoomBookingStatus.CANCELLED_BY_USER,
  RoomBookingStatus.CANCELLED_BY_HOST,
  RoomBookingStatus.CANCELLED_DUE_AMOUNT_BLOCK_EXPIRATION,
  RoomBookingStatus.CANCELLED_DUE_NO_RESPONSE_FROM_HOST,
]);

const seriesHasMixedConfirmedStates = (seriesBookings: HostRoomBooking[]) => {
  const hasConfirmed = seriesBookings.some((b) => HOST_CONFIRMED_STATUSES.has(b.status));
  const hasNonConfirmed = seriesBookings.some((b) => !HOST_CONFIRMED_STATUSES.has(b.status));
  return hasConfirmed && hasNonConfirmed;
};

export default function BookingRequestsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [bookings, setBookings] = useState<HostRoomBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [collectPaymentDialogOpen, setCollectPaymentDialogOpen] = useState(false);
  const [proposeChangeDialogOpen, setProposeChangeDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<HostRoomBooking | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [spaceTimeZone, setSpaceTimeZone] = useState<string>('');
  const [availableRooms, setAvailableRooms] = useState<Room[]>([]);
  const [originalRoomId, setOriginalRoomId] = useState<string>('');
  const [selectedRoomId, setSelectedRoomId] = useState<string>('');
  const [selectedRoomSummary, setSelectedRoomSummary] = useState<RoomSummary | null>(null);
  const [organizationBookingSlotsConfig, setOrganizationBookingSlotsConfig] =
    useState<BookingSlotsConfig | null>(null);
  const [isStepValid, setIsStepValid] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [originalSelectedDateTime, setOriginalSelectedDateTime] = useState<Date | null>(null);
  const [selectedDuration, setSelectedDuration] = useState<number>(60);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [proposingChange, setProposingChange] = useState(false);
  const [cancellingChange, setCancellingChange] = useState<string | null>(null);
  const [processingAction, setProcessingAction] = useState<ProcessingState | null>(null);
  const [declineTargets, setDeclineTargets] = useState<HostRoomBooking[]>([]);
  const [hostNegativeDialogMode, setHostNegativeDialogMode] = useState<HostNegativeDialogMode>('decline');
  const [autoAcceptRoomBooking, setAutoAcceptRoomBooking] = useState<boolean>(true);
  const [acceptConflictBookingId, setAcceptConflictBookingId] = useState<string | null>(null);
  const [acceptConflictDialogOpen, setAcceptConflictDialogOpen] = useState(false);
  /** Room booking ids whose full session row/card is shown (toggled via date chips on recurring cards). */
  const [expandedSessionBookingIds, setExpandedSessionBookingIds] = useState<Record<string, boolean>>({});
  const [seriesUnchecked, setSeriesUnchecked] = useState<Record<string, string[]>>({});
  const [seriesConflictIds, setSeriesConflictIds] = useState<string[]>([]);
  const [partialAccept, setPartialAccept] = useState<{
    seriesId: string;
    charged: HostRoomBooking[];
    declined: HostRoomBooking[];
  } | null>(null);
  const [partialAcceptReason, setPartialAcceptReason] = useState('');
  const [copiedPasscodeBookingId, setCopiedPasscodeBookingId] = useState<string | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const notificationBookingId = searchParams.get('bookingId')?.trim() || null;

  useEffect(() => {
    if (!notificationBookingId) return;
    let cancelled = false;
    (async () => {
      let roomLookupHttpStatus: number | null = null;
      try {
        await hostApiClient.getHostRoomBookingDetails(notificationBookingId);
        roomLookupHttpStatus = 200;
      } catch (error) {
        const status = (error as { response?: { status?: number } })?.response?.status;
        roomLookupHttpStatus = typeof status === 'number' ? status : null;
      }
      if (cancelled) return;
      const target = hostRequestsQueryBookingTarget(notificationBookingId, roomLookupHttpStatus);
      if (target) {
        navigate(target, { replace: true });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, notificationBookingId]);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const response = await hostApiClient.getHostRoomBookings(page, rowsPerPage);
      console.log('Fetched bookings:', response.content);
      
      setBookings(response.content);
      setTotalElements(response.page.totalElements);
      setAcceptConflictBookingId((current) =>
        current && response.content.some((booking) => booking.roomBookingId === current) ? current : null
      );
      setError(null);
    } catch (error) {
      console.error('Error fetching booking requests:', error);
      setError(t('roomBookingRequests.fetchError'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, t]);

  const displayRows = useMemo(() => groupHostRoomBookingRequests(bookings), [bookings]);

  const toggleSessionBookingExpanded = (roomBookingId: string) => {
    setExpandedSessionBookingIds((prev) => ({ ...prev, [roomBookingId]: !prev[roomBookingId] }));
  };

  const isSessionBookingExpanded = (roomBookingId: string) =>
    Boolean(expandedSessionBookingIds[roomBookingId]);

  /** Host may accept only while the booking still awaits their decision. */
  const canAcceptBooking = (booking: HostRoomBooking) =>
    !isPastBooking(booking.start) && booking.status === RoomBookingStatus.PENDING_CONFIRMATION;

  /** Host may decline only while the booking still awaits their decision (not after cancel/confirm/final states). */
  const canDeclineBooking = (booking: HostRoomBooking) =>
    !isPastBooking(booking.start) && booking.status === RoomBookingStatus.PENDING_CONFIRMATION;

  /** Host may cancel a booking they (or auto-accept) already confirmed, before the session starts. */
  const canHostCancelApprovedBooking = (booking: HostRoomBooking) =>
    !isPastBooking(booking.start) && HOST_CONFIRMED_STATUSES.has(booking.status);

  const canProposeChangeForBooking = (booking: HostRoomBooking) =>
    (booking.status === RoomBookingStatus.PENDING_CONFIRMATION ||
      HOST_CONFIRMED_STATUSES.has(booking.status)) &&
    (!booking.changeProposal ||
      booking.changeProposal.status === RoomBookingChangeStatus.DECLINED ||
      booking.changeProposal.status === RoomBookingChangeStatus.ACCEPTED) &&
    !isPastBooking(booking.start);

  const isBookingCancelled = (booking: HostRoomBooking) =>
    CANCELLED_BOOKING_STATUSES.has(booking.status);

  /** Manual Stripe refund for online bookings that were paid and not yet refunded. */
  const canRefundBooking = (booking: HostRoomBooking) =>
    booking.paymentMethod === PaymentMethod.ONLINE &&
    booking.paymentStatus === PaymentStatus.PAID &&
    (!booking.stripeRefundStatus || !REFUND_BLOCKED_STATUSES.has(booking.stripeRefundStatus));

  const canCollectPayment = (booking: HostRoomBooking) => canCollectRoomBookingPayment(booking);

  /**
   * Sessions on a merged series card are accepted or declined from that card.
   * An approved session, and a lone pending session, keep their own actions.
   */
  const recurringOnlineIndividualActionsAllowed = (booking: HostRoomBooking) =>
    !hostSeriesCardOwnsRoomActions(booking, bookings);

  useEffect(() => {
    fetchBookings();
    fetchSpaceTimeZone();
  }, [fetchBookings]);

  useEffect(() => {
    let cancelled = false;
    hostApiClient
      .getOrganization()
      .then((org) => {
        if (!cancelled) {
          setOrganizationBookingSlotsConfig(org.bookingSlotsConfig ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOrganizationBookingSlotsConfig(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchSpaceTimeZone = async () => {
    try {
      // Get the spaces the host has
      const spaces = await hostApiClient.getSpaces();
      // If there are spaces, use the timezone of the first space
      if (spaces && spaces.length > 0 && spaces[0].timezone) {
        setSpaceTimeZone(spaces[0].timezone);
      } else {
        // Fallback to browser timezone
        setSpaceTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    } catch (error) {
      console.error('Error fetching space timezone:', error);
      // Fallback to browser timezone
      setSpaceTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
    }
  };

  const isValidUUID = (uuid: string): boolean => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  };

  const isBookingActionInProgress = (bookingId: string | null | undefined) =>
    Boolean(
      bookingId &&
        processingAction &&
        'bookingId' in processingAction &&
        processingAction.bookingId === bookingId
    );

  const isBookingActionLoading = (
    bookingId: string | null | undefined,
    action: BookingActionType
  ) =>
    Boolean(
      bookingId &&
        processingAction &&
        'bookingId' in processingAction &&
        processingAction.bookingId === bookingId &&
        processingAction.action === action
    );

  const isSeriesActionInProgress = (seriesId: string | null | undefined) =>
    Boolean(
      seriesId &&
        processingAction &&
        'seriesId' in processingAction &&
        processingAction.seriesId === seriesId
    );

  const isSeriesActionLoading = (seriesId: string | null | undefined, action: SeriesProcessingAction) =>
    Boolean(
      seriesId &&
        processingAction &&
        'seriesId' in processingAction &&
        processingAction.seriesId === seriesId &&
        processingAction.action === action
    );

  const isHostNegativeDialogLoading = () => {
    if (!processingAction) return false;
    if ('seriesId' in processingAction && processingAction.action === 'declineAll') {
      return hostNegativeDialogMode === 'decline' && declineTargets.some((t) => t.recurringSeriesId === processingAction.seriesId);
    }
    if ('bookingId' in processingAction && processingAction.action === 'decline') {
      return hostNegativeDialogMode === 'decline' && selectedBooking?.roomBookingId === processingAction.bookingId;
    }
    if ('bookingId' in processingAction && processingAction.action === 'cancel') {
      return hostNegativeDialogMode === 'cancel' && selectedBooking?.roomBookingId === processingAction.bookingId;
    }
    return false;
  };

  const handleAccept = async (booking: HostRoomBooking) => {
    if (!canAcceptBooking(booking)) {
      return;
    }
    if (!booking.roomBookingId || !isValidUUID(booking.roomBookingId)) {
      console.error('Invalid booking ID:', booking.roomBookingId);
      setError(t('roomBookingRequests.invalidBookingId'));
      return;
    }

    setProcessingAction({ bookingId: booking.roomBookingId, action: 'accept' });
    try {
      setAcceptConflictBookingId(null);
      setAcceptConflictDialogOpen(false);
      if (pendingOnlineRoomUsesSeriesEndpoint(booking)) {
        await hostApiClient.acceptRoomBookingSeries(booking.recurringSeriesId!.trim());
      } else {
        await hostApiClient.acceptRoomBooking(booking.roomBookingId);
      }
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setError(null);
    } catch (error: any) {
      console.error('Error accepting booking:', error);
      if (error?.response?.status === 409) {
        setAcceptConflictBookingId(booking.roomBookingId);
        setAcceptConflictDialogOpen(true);
        setError(null);
      } else {
        setError(t('roomBookingRequests.acceptError'));
      }
    } finally {
      setProcessingAction(null);
    }
  };

  const selectedSeriesBookings = (seriesId: string, seriesBookings: HostRoomBooking[]) => {
    const unchecked = new Set(seriesUnchecked[seriesId] ?? []);
    const conflicts = new Set(seriesConflictIds);
    return seriesBookings.filter(
      (booking) =>
        canAcceptBooking(booking) &&
        !unchecked.has(booking.roomBookingId) &&
        !conflicts.has(booking.roomBookingId),
    );
  };

  const rememberSeriesConflicts = (seriesId: string, seriesBookings: HostRoomBooking[], error: unknown) => {
    const ids = bookingIdsMentionedInError(
      error,
      seriesBookings.map((booking) => booking.roomBookingId),
    );
    if (ids.length === 0) return;
    setSeriesConflictIds((current) => Array.from(new Set([...current, ...ids])));
    setSeriesUnchecked((current) => ({
      ...current,
      [seriesId]: Array.from(new Set([...(current[seriesId] ?? []), ...ids])),
    }));
  };

  const executeSeriesAccept = async (
    seriesId: string,
    seriesBookings: HostRoomBooking[],
    declineReason?: string,
  ) => {
    const selected = selectedSeriesBookings(seriesId, seriesBookings);
    const online = seriesBookings.every((booking) => booking.paymentMethod === PaymentMethod.ONLINE);
    const plan = planSeriesAccept({
      memberIds: seriesBookings.map((booking) => booking.roomBookingId),
      selectedIds: selected.map((booking) => booking.roomBookingId),
      conflictIds: seriesConflictIds,
      online,
    });
    if (!plan || !isValidUUID(seriesId)) {
      if (!isValidUUID(seriesId)) setError(t('roomBookingRequests.invalidBookingId'));
      return;
    }
    if (plan.kind === 'selected-online' && !declineReason) {
      const selectedIds = new Set(plan.selectedIds);
      setPartialAcceptReason('');
      setPartialAccept({
        seriesId,
        charged: seriesBookings.filter((booking) => selectedIds.has(booking.roomBookingId)),
        declined: seriesBookings.filter((booking) => !selectedIds.has(booking.roomBookingId)),
      });
      return;
    }

    setProcessingAction({ seriesId, action: 'acceptAll' });
    try {
      if (plan.kind === 'series') {
        await hostApiClient.acceptRoomBookingSeries(seriesId);
      } else if (plan.kind === 'selected-online') {
        await hostApiClient.acceptSelectedRoomBookingSeries(seriesId, plan.selectedIds, declineReason!);
      } else {
        for (const bookingId of plan.selectedIds) {
          await hostApiClient.acceptRoomBooking(bookingId);
        }
      }
      setPartialAccept(null);
      setPartialAcceptReason('');
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setError(null);
    } catch (error: any) {
      console.error('Error accepting recurring bookings:', error);
      if (error?.response?.status === 409) {
        rememberSeriesConflicts(seriesId, seriesBookings, error);
        setError(t('roomBookingRequests.acceptConflict', 'Some sessions overlap another booking.'));
      } else {
        setError(t('roomBookingRequests.acceptError'));
      }
      await fetchBookings();
    } finally {
      setProcessingAction(null);
    }
  };

  const handleAcceptAllSeries = async (seriesId: string, seriesBookings: HostRoomBooking[]) => {
    await executeSeriesAccept(seriesId, seriesBookings);
  };

  const handleHostNegativeDialogSubmit = async () => {
    const isDecline = hostNegativeDialogMode === 'decline';
    const predicate = isDecline ? canDeclineBooking : canHostCancelApprovedBooking;
    const targets = declineTargets.filter(
      (b) => b.roomBookingId && isValidUUID(b.roomBookingId) && predicate(b)
    );
    if (targets.length === 0) {
      setError(t('roomBookingRequests.noBookingSelected'));
      return;
    }

    const reason = declineReason.trim();
    if (!reason) {
      return;
    }

    const sharedSeriesId = targets[0].recurringSeriesId?.trim() ?? '';
    const useSeriesDecline = isDecline && isValidUUID(sharedSeriesId) && declineTargetsUseSeriesEndpoint(targets);

    if (isDecline && useSeriesDecline) {
      setProcessingAction({
        seriesId: sharedSeriesId,
        action: 'declineAll',
      });
    } else {
      setProcessingAction({
        bookingId: targets[0].roomBookingId,
        action: isDecline ? 'decline' : 'cancel',
      });
    }
    try {
      if (useSeriesDecline) {
        await hostApiClient.declineRoomBookingSeries(sharedSeriesId, reason);
      } else {
        for (const b of targets) {
          await hostApiClient.declineRoomBooking(b.roomBookingId, reason);
        }
      }
      setDeclineDialogOpen(false);
      setDeclineReason('');
      setDeclineTargets([]);
      setHostNegativeDialogMode('decline');
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setError(null);
    } catch (error) {
      console.error('Error declining booking:', error);
      setError(t('roomBookingRequests.declineError'));
    } finally {
      setProcessingAction(null);
    }
  };

  const openDeclineDialog = (targets: HostRoomBooking[]) => {
    const filtered = targets.filter((b) => canDeclineBooking(b));
    if (filtered.length === 0) return;
    setHostNegativeDialogMode('decline');
    setDeclineTargets(filtered);
    setSelectedBooking(filtered[0]);
    setDeclineReason('');
    setDeclineDialogOpen(true);
  };

  const openCancelDialog = (targets: HostRoomBooking[]) => {
    const filtered = targets.filter((b) => canHostCancelApprovedBooking(b));
    if (filtered.length === 0) return;
    setHostNegativeDialogMode('cancel');
    setDeclineTargets(filtered);
    setSelectedBooking(filtered[0]);
    setDeclineReason('');
    setDeclineDialogOpen(true);
  };

  const closeHostNegativeDialog = () => {
    if (isHostNegativeDialogLoading()) return;
    setDeclineDialogOpen(false);
    setDeclineReason('');
    setDeclineTargets([]);
    setHostNegativeDialogMode('decline');
  };

  const openBookingDetails = (booking: HostRoomBooking) => {
    navigate(roomBookingDetailsPath('host', booking.roomBookingId));
  };

  const openRefundDialog = (booking: HostRoomBooking) => {
    if (!canRefundBooking(booking)) return;
    setSelectedBooking(booking);
    setRefundReason('');
    setRefundDialogOpen(true);
  };

  const closeRefundDialog = () => {
    if (selectedBooking && isBookingActionLoading(selectedBooking.roomBookingId, 'refund')) {
      return;
    }
    setRefundDialogOpen(false);
    setRefundReason('');
  };

  const handleRefundSubmit = async () => {
    if (!selectedBooking?.roomBookingId || !canRefundBooking(selectedBooking)) {
      return;
    }
    if (!isValidUUID(selectedBooking.roomBookingId)) {
      setError(t('roomBookingRequests.invalidBookingId'));
      return;
    }

    setProcessingAction({ bookingId: selectedBooking.roomBookingId, action: 'refund' });
    try {
      const trimmed = refundReason.trim();
      await hostApiClient.refundRoomBooking(
        selectedBooking.roomBookingId,
        trimmed ? { refund_reason: trimmed } : {},
      );
      setRefundDialogOpen(false);
      setRefundReason('');
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setError(null);
    } catch (refundError) {
      console.error('Error issuing refund:', refundError);
      setError(t('roomBookingRequests.refundError', 'Failed to issue refund'));
    } finally {
      setProcessingAction(null);
    }
  };

  const renderRefundActionButton = (
    booking: HostRoomBooking,
    opts?: { size?: 'medium' | 'small'; fullWidth?: boolean },
  ) => {
    if (!ROOM_BOOKING_REFUND_UI_ENABLED || !canRefundBooking(booking)) {
      return null;
    }

    return (
      <Button
        variant="outlined"
        color="warning"
        size={opts?.size ?? 'medium'}
        fullWidth={opts?.fullWidth ?? true}
        onClick={() => openRefundDialog(booking)}
        disabled={isBookingActionInProgress(booking.roomBookingId)}
        startIcon={
          isBookingActionLoading(booking.roomBookingId, 'refund') ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <ReceiptLongOutlinedIcon fontSize="small" />
          )
        }
      >
        {t('roomBookingRequests.issueRefund', 'Issue refund')}
      </Button>
    );
  };

  const openCollectPaymentDialog = (booking: HostRoomBooking) => {
    if (!canCollectPayment(booking)) return;
    setSelectedBooking(booking);
    setCollectPaymentDialogOpen(true);
  };

  const closeCollectPaymentDialog = () => {
    if (selectedBooking && isBookingActionLoading(selectedBooking.roomBookingId, 'collect')) {
      return;
    }
    setCollectPaymentDialogOpen(false);
  };

  const handleCollectPaymentSubmit = async () => {
    if (!selectedBooking?.roomBookingId || !canCollectPayment(selectedBooking)) {
      return;
    }
    if (!isValidUUID(selectedBooking.roomBookingId)) {
      setError(t('roomBookingRequests.invalidBookingId'));
      return;
    }

    setProcessingAction({ bookingId: selectedBooking.roomBookingId, action: 'collect' });
    try {
      await hostApiClient.collectRoomBookingPayment(selectedBooking.roomBookingId);
      setCollectPaymentDialogOpen(false);
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setError(null);
    } catch (collectError) {
      console.error('Error collecting payment:', collectError);
      setError(
        t('roomBookingRequests.collectPaymentError', 'Failed to mark payment as collected'),
      );
    } finally {
      setProcessingAction(null);
    }
  };

  const renderCollectPaymentActionButton = (
    booking: HostRoomBooking,
    opts?: { size?: 'medium' | 'small'; fullWidth?: boolean },
  ) => {
    if (!canCollectPayment(booking)) {
      return null;
    }

    return (
      <Button
        variant="outlined"
        color="success"
        size={opts?.size ?? 'medium'}
        fullWidth={opts?.fullWidth ?? true}
        onClick={() => openCollectPaymentDialog(booking)}
        disabled={isBookingActionInProgress(booking.roomBookingId)}
        startIcon={
          isBookingActionLoading(booking.roomBookingId, 'collect') ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <PaymentsRoundedIcon fontSize="small" />
          )
        }
      >
        {t('roomBookingRequests.collectPayment', 'Mark as paid')}
      </Button>
    );
  };

  const renderRefundReasonNote = (reason?: string | null) =>
    reason ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        {t('roomBookingRequests.refundReasonNote', 'Refund note: {{reason}}', { reason })}
      </Typography>
    ) : null;

  const handleProposeChange = async (booking: HostRoomBooking) => {
    setSelectedBooking(booking);
    setProcessingAction({ bookingId: booking.roomBookingId, action: 'propose' });
    setLoadingRooms(true);
    setProposeChangeDialogOpen(true);
    
    try {
      if (!booking.spaceId) {
        setError(t('roomBookingRequests.spaceNotFound'));
        setProposeChangeDialogOpen(false);
        return;
      }

      const rooms = await hostApiClient.getRooms(booking.spaceId);
      setAvailableRooms(rooms);

      const currentRoomId =
        (booking.roomId && rooms.some((r) => r.id === booking.roomId) ? booking.roomId : null) ??
        rooms[0]?.id ??
        null;

      if (currentRoomId) {
        setOriginalRoomId(currentRoomId);
        setSelectedRoomId(currentRoomId);
        try {
          const roomSummary = await apiClient.getRoomSummary(currentRoomId);
          setSelectedRoomSummary(roomSummary);
        } catch (err) {
          console.error('Error fetching room summary:', err);
        }
      }
      
      // Set initial date/time from booking
      const startDate = new Date(booking.start);
      setSelectedDateTime(startDate);
      setOriginalSelectedDateTime(startDate);
      const duration = calculateDuration(booking.start, booking.end);
      setSelectedDuration(duration);
    } catch (error) {
      console.error('Error loading rooms:', error);
      setError(t('roomBookingRequests.errorLoadingRooms'));
    } finally {
      setLoadingRooms(false);
      setProcessingAction(null);
    }
  };

  const handleRoomChange = async (roomId: string) => {
    setSelectedRoomId(roomId);
    setIsStepValid(false);
    try {
      const roomSummary = await apiClient.getRoomSummary(roomId);
      setSelectedRoomSummary(roomSummary);
    } catch (err) {
      console.error('Error fetching room summary:', err);
      setError(t('roomBookingRequests.errorLoadingRoomDetails'));
    }
  };

  const handleProposeChangeSubmit = async () => {
    if (!selectedBooking || !isStepValid || !selectedDateTime) {
      return;
    }

    // Get the space timezone from room summary
    const timezone = selectedRoomSummary?.spaceTimeZone || spaceTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    // selectedDateTime is in browser local timezone; treat components as space-local.
    const year = selectedDateTime.getFullYear();
    const month = selectedDateTime.getMonth() + 1;
    const day = selectedDateTime.getDate();
    const hours = selectedDateTime.getHours();
    const minutes = selectedDateTime.getMinutes();
    const seconds = selectedDateTime.getSeconds();
    const spaceTimeString = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    const utcDate = zonedTimeToUtc(spaceTimeString, timezone);
    const formattedDateTime = utcDate.toISOString().replace('Z', '');
    const roomChanged = selectedRoomId !== originalRoomId;
    const startChanged = !!(
      selectedDateTime &&
      originalSelectedDateTime &&
      selectedDateTime.getTime() !== originalSelectedDateTime.getTime()
    );
    if (!roomChanged && !startChanged) {
      return;
    }

    setProposingChange(true);
    try {
      const request: ProposeChangeRequest = {
        newStartTime: formattedDateTime,
        newRoomId: roomChanged ? selectedRoomId : undefined,
        autoAcceptRoomBooking: autoAcceptRoomBooking,
      };

      await hostApiClient.proposeRoomBookingChange(selectedBooking.roomBookingId, request);
      handleCloseProposeChangeDialog();
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
      setError(null);
    } catch (error) {
      console.error('Error proposing change:', error);
      setError(t('roomBookingRequests.proposeChangeError'));
    } finally {
      setProposingChange(false);
    }
  };

  const hasProposeChangeDiff = useMemo(() => {
    if (!selectedBooking || !selectedDateTime) return false;
    const roomChanged = selectedRoomId !== originalRoomId;
    const startChanged = !!(
      originalSelectedDateTime &&
      selectedDateTime.getTime() !== originalSelectedDateTime.getTime()
    );
    return roomChanged || startChanged;
  }, [selectedBooking, selectedDateTime, originalSelectedDateTime, selectedRoomId, originalRoomId]);

  const handleCloseProposeChangeDialog = () => {
    setProposeChangeDialogOpen(false);
    setOriginalRoomId('');
    setSelectedRoomId('');
    setSelectedRoomSummary(null);
    setIsStepValid(false);
    setAutoAcceptRoomBooking(true);
    setSelectedDateTime(null);
    setOriginalSelectedDateTime(null);
    setAvailableRooms([]);
  };

  const getAcceptConflictBooking = () =>
    bookings.find((booking) => booking.roomBookingId === acceptConflictBookingId) ?? null;

  const closeAcceptConflictDialog = () => setAcceptConflictDialogOpen(false);

  const handleAcceptConflictPropose = () => {
    const booking = getAcceptConflictBooking();
    if (!booking) return;
    setAcceptConflictDialogOpen(false);
    handleProposeChange(booking);
  };

  const handleAcceptConflictReject = () => {
    const booking = getAcceptConflictBooking();
    if (!booking) return;
    setAcceptConflictDialogOpen(false);
    openDeclineDialog([booking]);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date and time
  const formatDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    
    return new Date(dateTimeString).toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  const formatDateTimeInSpaceTimezone = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    const timezone = spaceTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const utcLike = dateTimeString.endsWith('Z') ? dateTimeString : `${dateTimeString}Z`;
    return new Intl.DateTimeFormat(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone,
    }).format(new Date(utcLike));
  };

  const formatSessionDateChip = (dateTimeString: string) => {
    if (!dateTimeString) return '';
    const date = new Date(dateTimeString);
    if (Number.isNaN(date.getTime())) return dateTimeString;
    const day = date.toLocaleDateString(i18n.language, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const time = date.toLocaleTimeString(i18n.language, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
    return `${day}, ${time}`;
  };

  // Calculate duration in minutes
  const calculateDuration = (startTime: string, endTime: string) => {
    if (!startTime || !endTime) return 0;
    
    // Add 'Z' to ensure proper UTC parsing
    const start = new Date(startTime.endsWith('Z') ? startTime : startTime + 'Z');
    const end = new Date(endTime.endsWith('Z') ? endTime : endTime + 'Z');
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return 0;
    
    // Calculate duration in minutes
    const durationMs = end.getTime() - start.getTime();
    return Math.round(durationMs / (1000 * 60));
  };

  // Check if booking is in the past
  const isPastBooking = (startTime: string): boolean => {
    if (!startTime) return false;
    
    // Parse the booking start time (handle both with and without 'Z')
    const bookingStart = new Date(startTime.endsWith('Z') ? startTime : startTime + 'Z');
    
    // Check if date is valid
    if (isNaN(bookingStart.getTime())) return false;
    
    // Compare with current time
    const now = new Date();
    return bookingStart.getTime() < now.getTime();
  };

  const renderPastChip = (startTime: string, size: 'small' | 'medium' = 'small') => {
    if (!isPastBooking(startTime)) {
      return null;
    }

    return (
      <Chip
        icon={<HistoryIcon />}
        label={t('roomBookingRequests.past', 'Past')}
        size={size}
        variant="outlined"
        color="default"
        sx={{ width: 'fit-content', maxWidth: '100%', alignSelf: 'flex-start' }}
      />
    );
  };

  // Format duration
  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    
    if (hours === 0) {
      return `${remainingMinutes} ${t('roomBookingRequests.min')}`;
    } else if (remainingMinutes === 0) {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')}`;
    } else {
      return `${hours} ${hours > 1 ? t('roomBookingRequests.hrs') : t('roomBookingRequests.hr')} ${remainingMinutes} ${t('roomBookingRequests.min')}`;
    }
  };

  // Format additional gears
  const formatAdditionalGears = (gears: AdditionalGear[]) => {
    if (!gears || gears.length === 0) {
      return t('roomBookingRequests.noAdditionalGears');
    }
    return gears.map(gear => `${t(`gears.${gear.name}`)} (${gear.details})`).join(', ');
  };

  // Format date and time for UTC times (e.g., change proposals) - without duration
  const formatDateTimeUTC = (startTime: string) => {
    if (!startTime) return '';
    
    // Parse as UTC (backend sends UTC times without 'Z')
    const date = new Date(startTime.endsWith('Z') ? startTime : startTime + 'Z');
    return date.toLocaleString(i18n.language, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  // Get change proposal status description
  const getChangeProposalStatusDescription = (status: RoomBookingChangeStatus): string => {
    switch (status) {
      case RoomBookingChangeStatus.PENDING_REPLY:
        return t('bookings.changeProposalStatus.pending_reply', 'Pending Reply');
      case RoomBookingChangeStatus.ACCEPTED:
        return t('bookings.changeProposalStatus.accepted', 'Accepted');
      case RoomBookingChangeStatus.DECLINED:
        return t('bookings.changeProposalStatus.declined', 'Declined');
      case RoomBookingChangeStatus.CANCELLED:
        return t('bookings.changeProposalStatus.cancelled', 'Cancelled');
      case RoomBookingChangeStatus.EXPIRED:
        return t('bookings.changeProposalStatus.expired', 'Expired');
      default:
        return status;
    }
  };

  // Get change proposal status color
  const getChangeProposalStatusColor = (status: RoomBookingChangeStatus) => {
    switch (status) {
      case RoomBookingChangeStatus.PENDING_REPLY:
        return 'warning';
      case RoomBookingChangeStatus.ACCEPTED:
        return 'success';
      case RoomBookingChangeStatus.DECLINED:
        return 'error';
      case RoomBookingChangeStatus.CANCELLED:
        return 'default';
      case RoomBookingChangeStatus.EXPIRED:
        return 'default';
      default:
        return 'default';
    }
  };

  // Handle cancel change proposal
  const handleCancelChangeProposal = async (bookingId: string, changeId: string) => {
    const booking = bookings.find((b) => b.roomBookingId === bookingId);
    if (!booking || isBookingCancelled(booking)) {
      return;
    }
    setCancellingChange(changeId);
    setError(null);
    try {
      const request: CancelChangeRequest = {
        reason: undefined
      };
      await hostApiClient.cancelRoomBookingChange(bookingId, changeId, request);
      await fetchBookings();
      notifyRoomBookingNeedsActionCountChanged();
    } catch (error) {
      console.error('Error cancelling change proposal:', error);
      setError(t('roomBookingRequests.cancelChangeError', 'Failed to cancel change proposal'));
    } finally {
      setCancellingChange(null);
    }
  };

  // Format payment amount with currency
  const formatPaymentAmount = (amount: number, currencyCode: string) => {
    return new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  // Get payment status color
  const getPaymentStatusColor = (status: string | null | undefined) => {
    if (!status) return 'default';
    
    switch (status) {
      case 'PAID':
        return 'success';
      case 'AMOUNT_BLOCKED':
        return 'warning';
      case 'BLOCK_REQUESTED':
        return 'info';
      case 'TO_BE_PAID_ON_SITE':
        return 'default';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get refund status color
  const getRefundStatusColor = (status: string | null | undefined) => {
    if (!status) return 'default';
    
    switch (status) {
      case 'SUCCEEDED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'FAILED':
      case 'CANCELED':
        return 'error';
      case 'REQUIRES_ACTION':
        return 'info';
      default:
        return 'default';
    }
  };

  const getPasscodeStatusLabel = (status: BookingPasscodeStatus) =>
    t(`passcodeStatus.${status.toLowerCase()}`, status);

  const handleCopyPasscode = async (passcode: string | null | undefined, bookingId: string) => {
    if (!passcode) return;
    try {
      await navigator.clipboard.writeText(passcode);
      setCopiedPasscodeBookingId(bookingId);
      window.setTimeout(() => {
        setCopiedPasscodeBookingId((current) => (current === bookingId ? null : current));
      }, 1500);
    } catch (copyError) {
      console.error('Failed to copy passcode:', copyError);
    }
  };

  // Get payment method icon
  const getPaymentMethodIcon = (method: string) => {
    switch (method?.toUpperCase()) {
      case 'CASH':
        return <AttachMoneyIcon fontSize="small" />;
      case 'ONLINE':
        return <CreditCardIcon fontSize="small" />;
      default:
        return null;
    }
  };

  const changeProposalRowSx = {
    bgcolor: (theme: { palette: { mode: string } }) =>
      theme.palette.mode === 'dark' ? 'rgba(79, 95, 235, 0.08)' : 'rgba(79, 95, 235, 0.04)',
    position: 'relative' as const,
    '& td': {
      borderTop: 'none',
      borderBottom: 'none',
      verticalAlign: 'middle' as const,
      py: 0.5,
    },
  };

  const renderChangeProposalRow = (booking: HostRoomBooking) => {
    if (!booking.changeProposal) {
      return null;
    }
    return (
      <TableRow key={`cp-${booking.roomBookingId}`} sx={changeProposalRowSx}>
        <TableCell>
          <Chip
            label={t('bookings.changeProposal.title', 'Change Proposal')}
            color="info"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {booking.changeProposal.newRoomId !== booking.changeProposal.oldRoomId && booking.changeProposal.newRoomName && (
              <Typography variant="body2" fontSize="0.875rem" fontWeight="600" color="info.main">
                <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                  {t('bookings.changeProposal.newRoom', 'New Room')}:
                </Box>
                {booking.changeProposal.newRoomName}
              </Typography>
            )}
            {(() => {
              const oldTimeStr = booking.changeProposal!.oldStartTime.endsWith('Z')
                ? booking.changeProposal!.oldStartTime
                : `${booking.changeProposal!.oldStartTime}Z`;
              const newTimeStr = booking.changeProposal!.newStartTime.endsWith('Z')
                ? booking.changeProposal!.newStartTime
                : `${booking.changeProposal!.newStartTime}Z`;
              const oldDate = new Date(oldTimeStr);
              const newDate = new Date(newTimeStr);
              const originalTime = oldDate.toISOString().slice(0, 16);
              const newTime = newDate.toISOString().slice(0, 16);
              const timeChanged = originalTime !== newTime;
              return timeChanged ? (
                <Typography variant="body2" fontSize="0.875rem" fontWeight="600">
                  <Box component="span" sx={{ fontWeight: 700, mr: 0.5 }}>
                    {t('bookings.changeProposal.newTime', 'New Time')}:
                  </Box>
                  {formatDateTimeUTC(booking.changeProposal!.newStartTime)}
                </Typography>
              ) : null;
            })()}
          </Box>
        </TableCell>
        <TableCell />
        <TableCell />
        <TableCell>
          <Chip
            label={getChangeProposalStatusDescription(booking.changeProposal.status)}
            color={getChangeProposalStatusColor(booking.changeProposal.status) as 'default'}
            size="small"
          />
        </TableCell>
        <TableCell>
          {booking.changeProposal.status === RoomBookingChangeStatus.PENDING_REPLY &&
            !isBookingCancelled(booking) &&
            recurringOnlineIndividualActionsAllowed(booking) && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  fullWidth
                  onClick={() => handleCancelChangeProposal(booking.roomBookingId, booking.changeProposal!.id)}
                  disabled={cancellingChange === booking.changeProposal!.id}
                  sx={{
                    width: 220,
                    borderColor: 'error.main',
                    color: 'error.main',
                  }}
                  startIcon={
                    cancellingChange === booking.changeProposal!.id ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {t('bookings.changeProposal.cancel', 'Cancel')}
                </Button>
              </Box>
            )}
        </TableCell>
      </TableRow>
    );
  };

  const renderDesktopSingleBookingFragment = (
    booking: HostRoomBooking,
    opts?: { nested?: boolean }
  ) => {
    const bandName = resolveHostRoomBookingBandName(booking);

    return (
    <Fragment key={booking.roomBookingId}>
      <TableRow
        sx={
          opts?.nested
            ? {
                bgcolor: 'action.hover',
                '& > .MuiTableCell-root': { verticalAlign: 'middle' },
              }
            : undefined
        }
      >
        <TableCell>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <RoomBookingUserAvatar
              userProfileImageUrl={booking.userProfileImageUrl}
              displayName={booking.userName}
              size={36}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
            <Typography variant="body2">{booking.userName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {booking.phoneNumber}
            </Typography>
            {bandName ? <UserBandAffiliationLine bandName={bandName} /> : null}
            <BookingCreatedAtLabel
              createdAt={booking.createdAt}
              timezone={booking.timezone}
              placement="inline"
              showTimezone={false}
            />
          </Box>
          </Stack>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {renderPastChip(booking.start)}
            <Box>
              <Typography variant="body2" color="text.secondary">
                {t('bookingSteps.participantCount')}: {booking.participantDetails?.participantCount || '-'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {t('roomBookingRequests.space')}: {booking.spaceName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('roomBookingRequests.room')}: {booking.roomName}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                {t('roomBookingRequests.dateTime')}: {formatDateTime(booking.start)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('roomBookingRequests.duration')}:{' '}
                {formatDuration(calculateDuration(booking.start, booking.end))}
              </Typography>
            </Box>
            {booking.passcodeInfo && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: 'background.default',
                  borderColor: 'divider',
                  display: 'inline-block',
                  width: 'fit-content',
                  maxWidth: '100%',
                }}
              >
                <Stack spacing={0.5}>
                  {booking.passcodeInfo.status !== BookingPasscodeStatus.SUCCESS && (
                    <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
                      {t(
                        'roomBookingRequests.passcodeGenerationIssue',
                        'Passcode generation issue: {{status}}',
                        { status: getPasscodeStatusLabel(booking.passcodeInfo.status) },
                      )}
                    </Alert>
                  )}
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <VpnKeyOutlinedIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', letterSpacing: '0.06em' }}>
                      {booking.passcodeInfo.passcode || t('bookingDetails.smartLockPasscodeUnavailable', 'Unavailable')}
                    </Typography>
                    {booking.passcodeInfo.passcode && (
                      <Tooltip
                        title={
                          copiedPasscodeBookingId === booking.roomBookingId
                            ? t('bookingDetails.passcodeCopied', 'Copied')
                            : t('bookingDetails.copyPasscode', 'Copy passcode')
                        }
                      >
                        <IconButton
                          size="small"
                          onClick={() => handleCopyPasscode(booking.passcodeInfo?.passcode, booking.roomBookingId)}
                          aria-label={t('bookingDetails.copyPasscode', 'Copy passcode')}
                        >
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <AccessTimeIcon fontSize="small" color="action" />
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTimeInSpaceTimezone(booking.passcodeInfo.validFrom)} - {formatDateTimeInSpaceTimezone(booking.passcodeInfo.validTo)}
                    </Typography>
                  </Stack>
                </Stack>
              </Paper>
            )}
          </Box>
        </TableCell>
        <TableCell>{formatAdditionalGears(booking.additionalGears)}</TableCell>
        <TableCell>
          <Stack spacing={0.5}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight="medium">
                {formatPaymentAmount(booking.totalAmount, booking.currencyCode)}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {getPaymentMethodIcon(booking.paymentMethod)}
                <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                  {booking.paymentMethod
                    ? t(`paymentMethod.${booking.paymentMethod.toLowerCase()}`)
                    : t('paymentMethod.unknown')}
                </Typography>
              </Box>
            </Box>
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {!booking.stripeRefundStatus && (
                <Chip
                  label={
                    booking.paymentStatus
                      ? t(`paymentStatus.${booking.paymentStatus.toLowerCase()}`)
                      : t('paymentStatus.unknown')
                  }
                  color={getPaymentStatusColor(booking.paymentStatus) as 'default'}
                  size="small"
                />
              )}
              {booking.stripeRefundStatus && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <ReceiptIcon fontSize="small" color="action" />
                  <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                    {t('refundStatus.label')}:
                  </Typography>
                  <Chip
                    label={t(`refundStatus.${booking.stripeRefundStatus.toLowerCase()}`)}
                    color={getRefundStatusColor(booking.stripeRefundStatus) as 'default'}
                    size="small"
                  />
                </Box>
              )}
              {renderRefundReasonNote(booking.refundReason)}
              {booking.pass && <PassBookingChip pass={booking.pass} />}
            </Stack>
          </Stack>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            <Chip
              label={t(`status.${booking.status.toLowerCase()}`, booking.status)}
              color={statusColors[booking.status as keyof typeof statusColors] || 'default'}
              size="small"
              sx={{
                maxWidth: { xs: '120px', sm: 'none' },
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          </Stack>
        </TableCell>
        <TableCell sx={opts?.nested ? { maxWidth: 280 } : undefined}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.75,
              width: opts?.nested ? '100%' : 220,
            }}
          >
            <Stack spacing={1}>
              {canAcceptBooking(booking) && recurringOnlineIndividualActionsAllowed(booking) && (
                <Button
                  variant="contained"
                  color="success"
                  size="medium"
                  fullWidth
                  onClick={() => handleAccept(booking)}
                  disabled={
                    isBookingActionInProgress(booking.roomBookingId) || !canAcceptBooking(booking)
                  }
                  startIcon={
                    isBookingActionLoading(booking.roomBookingId, 'accept') ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {t('roomBookingRequests.accept')}
                </Button>
              )}
              {canDeclineBooking(booking) && recurringOnlineIndividualActionsAllowed(booking) && (
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  fullWidth
                  onClick={() => openDeclineDialog([booking])}
                  disabled={
                    isBookingActionInProgress(booking.roomBookingId) || !canDeclineBooking(booking)
                  }
                  sx={{
                    borderColor: 'error.main',
                    color: 'error.main',
                  }}
                >
                  {t('roomBookingRequests.reject')}
                </Button>
              )}
              {canProposeChangeForBooking(booking) && recurringOnlineIndividualActionsAllowed(booking) && (
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  fullWidth
                  onClick={() => handleProposeChange(booking)}
                  disabled={isBookingActionInProgress(booking.roomBookingId)}
                  startIcon={
                    isBookingActionLoading(booking.roomBookingId, 'propose') ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {t('roomBookingRequests.proposeChange')}
                </Button>
              )}
              {canHostCancelApprovedBooking(booking) && recurringOnlineIndividualActionsAllowed(booking) && (
                <Button
                  variant="outlined"
                  color="error"
                  size="medium"
                  fullWidth
                  onClick={() => openCancelDialog([booking])}
                  disabled={
                    isBookingActionInProgress(booking.roomBookingId) ||
                    !canHostCancelApprovedBooking(booking)
                  }
                  sx={{
                    borderColor: 'error.main',
                    color: 'error.main',
                  }}
                  startIcon={
                    isBookingActionLoading(booking.roomBookingId, 'cancel') ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : undefined
                  }
                >
                  {t('roomBookingRequests.cancelApprovedBooking', 'Cancel')}
                </Button>
              )}
              {renderCollectPaymentActionButton(booking)}
              {renderRefundActionButton(booking)}
              <Button
                variant="outlined"
                size="medium"
                fullWidth
                onClick={() => openBookingDetails(booking)}
                disabled={isBookingActionInProgress(booking.roomBookingId)}
              >
                {t('roomBookingRequests.viewBooking', 'View booking')}
              </Button>
            </Stack>
          </Box>
        </TableCell>
      </TableRow>
      {renderChangeProposalRow(booking)}
    </Fragment>
    );
  };

  const toggleSeriesSessionSelected = (seriesId: string, roomBookingId: string, selected: boolean) => {
    setSeriesUnchecked((current) => {
      const next = new Set(current[seriesId] ?? []);
      if (selected) next.delete(roomBookingId);
      else next.add(roomBookingId);
      return { ...current, [seriesId]: Array.from(next) };
    });
  };

  const renderSeriesSessionPicker = (seriesId: string, seriesBookings: HostRoomBooking[]) => {
    const unchecked = new Set(seriesUnchecked[seriesId] ?? []);
    const conflicts = new Set(seriesConflictIds);
    const online = seriesBookings.every((booking) => booking.paymentMethod === PaymentMethod.ONLINE);
    const busy = isSeriesActionInProgress(seriesId);
    const selectedCount = seriesBookings.filter(
      (booking) => !unchecked.has(booking.roomBookingId) && !conflicts.has(booking.roomBookingId),
    ).length;
    return (
      <Box sx={{ mt: 0.75 }}>
        <Stack direction="row" flexWrap="wrap" useFlexGap gap={0.75} sx={{ maxWidth: 480 }}>
          {seriesBookings.map((booking) => {
            const conflict = conflicts.has(booking.roomBookingId);
            const checked = !conflict && !unchecked.has(booking.roomBookingId);
            const expanded = isSessionBookingExpanded(booking.roomBookingId);
            const dateLabel = formatSessionDateChip(booking.start);
            const selected = checked && !conflict;
            const pill = (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  maxWidth: '100%',
                  borderRadius: 999,
                  border: '1px solid',
                  borderColor: conflict ? 'error.main' : selected || expanded ? 'primary.main' : 'divider',
                  bgcolor: selected ? 'primary.main' : 'transparent',
                  color: selected ? 'primary.contrastText' : conflict ? 'error.main' : 'text.primary',
                  opacity: busy ? 0.6 : 1,
                }}
              >
                <Box
                  component="button"
                  type="button"
                  disabled={conflict || busy}
                  aria-pressed={checked}
                  aria-label={String(
                    t('roomBookingRequests.sessionSelectAria', 'Include session on {{date}}', {
                      date: dateLabel,
                    }),
                  )}
                  onClick={() => toggleSeriesSessionSelected(seriesId, booking.roomBookingId, !checked)}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    border: 0,
                    m: 0,
                    minWidth: 0,
                    bgcolor: 'transparent',
                    color: 'inherit',
                    font: 'inherit',
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    lineHeight: 1.4,
                    pl: 1,
                    pr: 0.25,
                    py: 0.4,
                    cursor: conflict || busy ? 'not-allowed' : 'pointer',
                  }}
                >
                  {selected ? (
                    <CheckRoundedIcon sx={{ fontSize: 16 }} />
                  ) : (
                    <CloseRoundedIcon sx={{ fontSize: 16 }} />
                  )}
                  {dateLabel}
                </Box>
                <IconButton
                  size="small"
                  aria-expanded={expanded}
                  aria-label={String(
                    t('roomBookingRequests.sessionChipToggleAria', 'Show or hide details for session on {{date}}', {
                      date: dateLabel,
                    }),
                  )}
                  onClick={() => toggleSessionBookingExpanded(booking.roomBookingId)}
                  sx={{ color: 'inherit', p: 0.25, mr: 0.25 }}
                >
                  {expanded ? (
                    <KeyboardArrowUpRoundedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Box>
            );
            return conflict ? (
              <Tooltip key={booking.roomBookingId} title={t('roomBookingRequests.acceptConflict', 'Some sessions overlap another booking.')}>
                {pill}
              </Tooltip>
            ) : (
              <Fragment key={booking.roomBookingId}>{pill}</Fragment>
            );
          })}
        </Stack>
        {selectedCount === 0 ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {t('roomBookingRequests.selectAtLeastOne', 'Select at least one session.')}
          </Typography>
        ) : null}
        {online && selectedCount > 0 && selectedCount < seriesBookings.length ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
            {t(
              'roomBookingRequests.onlineUncheckedWillBeDeclined',
              'Unchecked sessions will be declined.',
            )}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const renderSessionDateTapHint = () => (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75, lineHeight: 1.35 }}>
      {t(
        'roomBookingRequests.tapSessionDateHint',
        'Select the sessions to accept. The arrow opens that session.',
      )}
    </Typography>
  );

  return (
    <Box sx={{ py: 4, px: 2 }}>
      <Card elevation={3} sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 2 }}>
        {t('roomBookingRequests.title')}
      </Typography>
      <Divider sx={{ mb: 4 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : bookings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            {t('roomBookingRequests.noBookings')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('roomBookingRequests.bookingsWillAppearHere')}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
            <Stack spacing={2}>
              {displayRows.map((row) => {
                if (row.kind === 'single') {
                  const booking = row.booking;
                  const bandName = resolveHostRoomBookingBandName(booking);
                  return (
                <Card 
                  key={booking.roomBookingId} 
                  elevation={2}
                  sx={{ 
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                      <Chip 
                        label={t(`status.${booking.status.toLowerCase()}`, booking.status)} 
                        color={statusColors[booking.status as keyof typeof statusColors] || 'default'} 
                        size="medium"
                        sx={{
                          flex: '1 1 auto',
                          fontWeight: 600,
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            textAlign: 'center',
                            lineHeight: 1.3,
                            padding: '8px 12px'
                          }
                        }}
                      />
                    </Stack>

                    {/* User Info */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                      <RoomBookingUserAvatar
                        userProfileImageUrl={booking.userProfileImageUrl}
                        displayName={booking.userName}
                      />
                      <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', mb: 0.5 }}>
                        {booking.userName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {booking.phoneNumber}
                      </Typography>
                      {bandName ? <UserBandAffiliationLine bandName={bandName} /> : null}
                      <BookingCreatedAtLabel
                        createdAt={booking.createdAt}
                        timezone={booking.timezone}
                        placement="inline"
                        showTimezone={false}
                      />
                      </Box>
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Band and Space/Room Info */}
                    <Stack spacing={1.5} sx={{ mb: 2 }}>
                      {renderPastChip(booking.start, 'medium')}
                      {booking.participantDetails?.participantCount ? (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {t('bookingSteps.participantCount')}: {booking.participantDetails.participantCount}
                          </Typography>
                        </Box>
                      ) : null}

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                          {t('roomBookingRequests.location')}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {booking.spaceName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {booking.roomName}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                          {t('roomBookingRequests.dateTime')}
                        </Typography>
                        <Typography variant="body2" fontWeight="medium">
                          {formatDateTime(booking.start)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                          {formatDuration(calculateDuration(booking.start, booking.end))}
                        </Typography>
                      </Box>

                      {booking.passcodeInfo && (
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.25,
                            borderRadius: 1.5,
                            bgcolor: 'background.default',
                            borderColor: 'divider',
                            display: 'inline-block',
                            width: 'fit-content',
                            maxWidth: '100%',
                          }}
                        >
                          <Stack spacing={0.75}>
                            {booking.passcodeInfo.status !== BookingPasscodeStatus.SUCCESS && (
                              <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
                                {t(
                                  'roomBookingRequests.passcodeGenerationIssue',
                                  'Passcode generation issue: {{status}}',
                                  { status: getPasscodeStatusLabel(booking.passcodeInfo.status) },
                                )}
                              </Alert>
                            )}
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <VpnKeyOutlinedIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight={700} sx={{ fontFamily: 'monospace', letterSpacing: '0.06em' }}>
                                {booking.passcodeInfo.passcode || t('bookingDetails.smartLockPasscodeUnavailable', 'Unavailable')}
                              </Typography>
                              {booking.passcodeInfo.passcode && (
                                <Tooltip
                                  title={
                                    copiedPasscodeBookingId === booking.roomBookingId
                                      ? t('bookingDetails.passcodeCopied', 'Copied')
                                      : t('bookingDetails.copyPasscode', 'Copy passcode')
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handleCopyPasscode(booking.passcodeInfo?.passcode, booking.roomBookingId)}
                                    aria-label={t('bookingDetails.copyPasscode', 'Copy passcode')}
                                  >
                                    <ContentCopyIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <AccessTimeIcon fontSize="small" color="action" />
                              <Typography variant="caption" color="text.secondary">
                                {formatDateTimeInSpaceTimezone(booking.passcodeInfo.validFrom)} - {formatDateTimeInSpaceTimezone(booking.passcodeInfo.validTo)}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Paper>
                      )}

                      {booking.additionalGears && booking.additionalGears.length > 0 && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                            {t('roomBookingRequests.additionalGears')}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {formatAdditionalGears(booking.additionalGears)}
                          </Typography>
                        </Box>
                      )}
                    </Stack>

                    <Divider sx={{ mb: 2 }} />

                    {/* Payment Section */}
                    <Box sx={{ mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                          {t('bookings.payment')}
                        </Typography>
                        {/* Payment Status */}
                        {!booking.stripeRefundStatus && booking.paymentStatus && (
                          <Chip 
                            label={t(`paymentStatus.${booking.paymentStatus.toLowerCase()}`)}
                            color={getPaymentStatusColor(booking.paymentStatus) as any}
                            size="small" 
                          />
                        )}
                        {/* Refund Status */}
                        {booking.stripeRefundStatus && (
                          <Chip 
                            icon={<ReceiptIcon fontSize="small" />}
                            label={t(`refundStatus.${booking.stripeRefundStatus.toLowerCase()}`)}
                            color={getRefundStatusColor(booking.stripeRefundStatus) as any}
                            size="small" 
                          />
                        )}
                        {booking.pass && <PassBookingChip pass={booking.pass} />}
                      </Box>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        bgcolor: 'background.default',
                        p: 1.5,
                        borderRadius: 1,
                      }}>
                        <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.125rem' }}>
                          {formatPaymentAmount(booking.totalAmount, booking.currencyCode)}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getPaymentMethodIcon(booking.paymentMethod)}
                          <Typography variant="body2" color="text.secondary">
                            {booking.paymentMethod ? t(`paymentMethod.${booking.paymentMethod.toLowerCase()}`) : t('paymentMethod.unknown')}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Actions */}
                    <Stack spacing={1} sx={{ width: '100%' }}>
                      <Button
                        variant="outlined"
                        size="medium"
                        onClick={() => openBookingDetails(booking)}
                        disabled={isBookingActionInProgress(booking.roomBookingId)}
                        fullWidth
                      >
                        {t('roomBookingRequests.viewBooking', 'View booking')}
                      </Button>
                      {canAcceptBooking(booking) && recurringOnlineIndividualActionsAllowed(booking) && (
                        <Button
                          variant="contained"
                          color="success"
                          size="medium"
                          onClick={() => handleAccept(booking)}
                          disabled={
                            isBookingActionInProgress(booking.roomBookingId) ||
                            !canAcceptBooking(booking)
                          }
                          startIcon={
                            isBookingActionLoading(booking.roomBookingId, 'accept') ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : undefined
                          }
                          fullWidth
                        >
                          {t('roomBookingRequests.accept')}
                        </Button>
                      )}
                      {canDeclineBooking(booking) && recurringOnlineIndividualActionsAllowed(booking) && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="medium"
                          onClick={() => openDeclineDialog([booking])}
                          disabled={
                            isBookingActionInProgress(booking.roomBookingId) ||
                            !canDeclineBooking(booking)
                          }
                          fullWidth
                          sx={{
                            borderColor: 'error.main',
                            color: 'error.main',
                          }}
                        >
                          {t('roomBookingRequests.reject')}
                        </Button>
                      )}
                      {canProposeChangeForBooking(booking) && recurringOnlineIndividualActionsAllowed(booking) && (
                        <Button
                          variant="outlined"
                          color="primary"
                          size="medium"
                          onClick={() => handleProposeChange(booking)}
                          disabled={isBookingActionInProgress(booking.roomBookingId)}
                          startIcon={
                            isBookingActionLoading(booking.roomBookingId, 'propose') ? (
                              <CircularProgress size={16} color="inherit" />
                            ) : undefined
                          }
                          fullWidth
                        >
                          {t('roomBookingRequests.proposeChange')}
                        </Button>
                      )}
                        {canHostCancelApprovedBooking(booking) && recurringOnlineIndividualActionsAllowed(booking) && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="medium"
                            onClick={() => openCancelDialog([booking])}
                            disabled={
                              isBookingActionInProgress(booking.roomBookingId) ||
                              !canHostCancelApprovedBooking(booking)
                            }
                            fullWidth
                            sx={{
                              borderColor: 'error.main',
                              color: 'error.main',
                            }}
                            startIcon={
                              isBookingActionLoading(booking.roomBookingId, 'cancel') ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : undefined
                            }
                          >
                            {t('roomBookingRequests.cancelApprovedBooking', 'Cancel')}
                          </Button>
                        )}
                        {renderCollectPaymentActionButton(booking)}
                        {renderRefundActionButton(booking)}
                    </Stack>
                  </Box>
                </Card>
                  );
                }

                const seriesBookings = row.bookings;
                const rep = seriesBookings[0];
                const uniqueStatuses = Array.from(new Set(seriesBookings.map((b) => b.status)));
                const showSeriesPartiallyConfirmed = seriesHasMixedConfirmedStates(seriesBookings);
                const allSeriesSessionsPast = seriesBookings.every((b) => isPastBooking(b.start));
                const selectedSeries = selectedSeriesBookings(row.seriesId, seriesBookings);
                const seriesAllChecked =
                  selectedSeries.length === seriesBookings.length &&
                  !seriesBookings.some((booking) => seriesConflictIds.includes(booking.roomBookingId));
                const seriesDisplayTotal = selectedSeries.reduce((sum, booking) => sum + booking.totalAmount, 0);
                const eligibleDecline = seriesBookings.filter(canDeclineBooking);
                const paymentStatuses = Array.from(
                  new Set(
                    seriesBookings
                      .map((b) => b.paymentStatus)
                      .filter((x): x is PaymentStatus => x != null)
                  )
                );
                const repBandName = resolveHostRoomBookingBandName(rep);

                return (
                  <Card
                    key={row.seriesId}
                    elevation={2}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <Box sx={{ p: 2 }}>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                        {showSeriesPartiallyConfirmed ? (
                          <Chip
                            label={t('roomBookingRequests.partiallyConfirmedSeries', 'Partially confirmed')}
                            color="warning"
                            size="medium"
                            sx={{
                              fontWeight: 600,
                              '& .MuiChip-label': {
                                whiteSpace: 'normal',
                                textAlign: 'center',
                                lineHeight: 1.3,
                                padding: '8px 12px',
                              },
                            }}
                          />
                        ) : (
                          uniqueStatuses.map((st) => (
                            <Chip
                              key={st}
                              label={String(t(`status.${st.toLowerCase()}`, st))}
                              color={statusColors[st as keyof typeof statusColors] || 'default'}
                              size="medium"
                              sx={{
                                fontWeight: 600,
                                '& .MuiChip-label': {
                                  whiteSpace: 'normal',
                                  textAlign: 'center',
                                  lineHeight: 1.3,
                                  padding: '8px 12px',
                                },
                              }}
                            />
                          ))
                        )}
                        <Chip
                          icon={<RepeatIcon sx={{ fontSize: 18 }} />}
                          label={t('roomBookingRequests.recurringBooking', 'Recurring')}
                          color="secondary"
                          variant="outlined"
                          size="small"
                        />
                        <Chip
                          label={t('roomBookingRequests.sessionsCount', '{{count}} sessions', {
                            count: seriesBookings.length,
                          })}
                          size="small"
                          variant="outlined"
                        />
                      </Stack>

                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <RoomBookingUserAvatar
                          userProfileImageUrl={rep.userProfileImageUrl}
                          displayName={rep.userName}
                        />
                        <Box sx={{ minWidth: 0 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.125rem', mb: 0.5 }}>
                          {rep.userName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {rep.phoneNumber}
                        </Typography>
                        {repBandName ? <UserBandAffiliationLine bandName={repBandName} /> : null}
                        <BookingCreatedAtLabel
                          createdAt={rep.createdAt}
                          timezone={rep.timezone}
                          placement="inline"
                          showTimezone={false}
                        />
                        </Box>
                      </Stack>

                      <Divider sx={{ mb: 2 }} />

                      <Stack spacing={1.5} sx={{ mb: 2 }}>
                        {allSeriesSessionsPast && renderPastChip(rep.start, 'medium')}
                        {rep.participantDetails?.participantCount ? (
                          <Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {t('bookingSteps.participantCount')}: {rep.participantDetails.participantCount}
                            </Typography>
                          </Box>
                        ) : null}

                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                            {t('roomBookingRequests.location')}
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {rep.spaceName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                            {rep.roomName}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                            {t('roomBookingRequests.sessionDates', 'Session dates')}
                          </Typography>
                          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                            {renderSeriesSessionPicker(row.seriesId, seriesBookings)}
                          </Stack>
                          {renderSessionDateTapHint()}
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem', mt: 0.5 }}>
                            {formatDuration(calculateDuration(rep.start, rep.end))}{' '}
                            {t('roomBookingRequests.perSession', 'per session')}
                          </Typography>
                          {seriesBookings.some((b) => isSessionBookingExpanded(b.roomBookingId)) && (
                            <Stack
                              spacing={1.5}
                              sx={{
                                mt: 1.5,
                                pl: 1.5,
                                borderLeft: 2,
                                borderColor: 'divider',
                              }}
                            >
                              {seriesBookings
                                .filter((b) => isSessionBookingExpanded(b.roomBookingId))
                                .map((b) => (
                                <Paper key={b.roomBookingId} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
                                  <Stack spacing={1}>
                                    <Chip
                                      label={t(`status.${b.status.toLowerCase()}`, b.status)}
                                      color={statusColors[b.status as keyof typeof statusColors] || 'default'}
                                      size="small"
                                      sx={{ width: 'fit-content' }}
                                    />
                                    {renderPastChip(b.start)}
                                    <Typography variant="body2" fontWeight={600}>
                                      {formatDateTime(b.start)}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {formatDuration(calculateDuration(b.start, b.end))}
                                    </Typography>
                                    <Typography variant="body2" fontWeight="medium">
                                      {formatPaymentAmount(b.totalAmount, b.currencyCode)}
                                    </Typography>
                                    {b.pass && <PassBookingChip pass={b.pass} />}
                                    <Stack spacing={0.75}>
                                      {canAcceptBooking(b) && recurringOnlineIndividualActionsAllowed(b) && (
                                        <Button
                                          variant="contained"
                                          color="success"
                                          size="medium"
                                          fullWidth
                                          onClick={() => handleAccept(b)}
                                          disabled={
                                            isBookingActionInProgress(b.roomBookingId) || !canAcceptBooking(b)
                                          }
                                          startIcon={
                                            isBookingActionLoading(b.roomBookingId, 'accept') ? (
                                              <CircularProgress size={14} color="inherit" />
                                            ) : undefined
                                          }
                                        >
                                          {t('roomBookingRequests.accept')}
                                        </Button>
                                      )}
                                      {canDeclineBooking(b) && recurringOnlineIndividualActionsAllowed(b) && (
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          size="medium"
                                          fullWidth
                                          onClick={() => openDeclineDialog([b])}
                                          disabled={
                                            isBookingActionInProgress(b.roomBookingId) || !canDeclineBooking(b)
                                          }
                                        >
                                          {t('roomBookingRequests.reject')}
                                        </Button>
                                      )}
                                    {canProposeChangeForBooking(b) && recurringOnlineIndividualActionsAllowed(b) && (
                                      <Button
                                        variant="outlined"
                                        color="primary"
                                        size="medium"
                                        fullWidth
                                        onClick={() => handleProposeChange(b)}
                                        disabled={isBookingActionInProgress(b.roomBookingId)}
                                        startIcon={
                                          isBookingActionLoading(b.roomBookingId, 'propose') ? (
                                            <CircularProgress size={14} color="inherit" />
                                          ) : undefined
                                        }
                                      >
                                        {t('roomBookingRequests.proposeChange')}
                                      </Button>
                                    )}
                                      {canHostCancelApprovedBooking(b) && recurringOnlineIndividualActionsAllowed(b) && (
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          size="medium"
                                          fullWidth
                                          onClick={() => openCancelDialog([b])}
                                          disabled={
                                            isBookingActionInProgress(b.roomBookingId) ||
                                            !canHostCancelApprovedBooking(b)
                                          }
                                          sx={{
                                            borderColor: 'error.main',
                                            color: 'error.main',
                                          }}
                                          startIcon={
                                            isBookingActionLoading(b.roomBookingId, 'cancel') ? (
                                              <CircularProgress size={14} color="inherit" />
                                            ) : undefined
                                          }
                                        >
                                          {t('roomBookingRequests.cancelApprovedBooking', 'Cancel')}
                                        </Button>
                                      )}
                                      {renderCollectPaymentActionButton(b, { size: 'medium', fullWidth: true })}
                                      {renderRefundActionButton(b, { size: 'medium', fullWidth: true })}
                                    </Stack>
                                  </Stack>
                                </Paper>
                              ))}
                            </Stack>
                          )}
                        </Box>

                        {rep.additionalGears && rep.additionalGears.length > 0 && (
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                              {t('roomBookingRequests.additionalGears')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {formatAdditionalGears(rep.additionalGears)}
                            </Typography>
                          </Box>
                        )}
                      </Stack>

                      <Divider sx={{ mb: 2 }} />

                      <Box sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.7rem' }}>
                            {t('bookings.payment')}
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={0.5}>
                            {paymentStatuses.length === 1 && !seriesBookings.some((b) => b.stripeRefundStatus) && (
                              <Chip
                                label={t(`paymentStatus.${String(paymentStatuses[0]).toLowerCase()}`)}
                                color={getPaymentStatusColor(paymentStatuses[0] as string) as 'default'}
                                size="small"
                              />
                            )}
                            {paymentStatuses.length > 1 && (
                              <Chip label={t('roomBookingRequests.mixedPayment', 'Mixed payment states')} size="small" variant="outlined" />
                            )}
                            {seriesBookings.some((b) => b.stripeRefundStatus) && (
                              <Chip
                                icon={<ReceiptIcon fontSize="small" />}
                                label={t('roomBookingRequests.refundInSeries', 'Refund on one or more sessions')}
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                            {seriesBookings.find((b) => b.pass) && (
                              <PassBookingChip pass={seriesBookings.find((b) => b.pass)!.pass!} />
                            )}
                          </Stack>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            bgcolor: 'background.default',
                            p: 1.5,
                            borderRadius: 1,
                          }}
                        >
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {seriesAllChecked
                                ? t('roomBookingRequests.totalForSessions', 'Total for {{count}} sessions', {
                                    count: seriesBookings.length,
                                  })
                                : t(
                                    'roomBookingRequests.selectedSessionsCaption',
                                    '{{selected}} of {{total}} sessions',
                                    { selected: selectedSeries.length, total: seriesBookings.length },
                                  )}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '1.125rem' }}>
                              {formatPaymentAmount(seriesDisplayTotal, rep.currencyCode)}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {getPaymentMethodIcon(rep.paymentMethod)}
                            <Typography variant="body2" color="text.secondary">
                              {rep.paymentMethod ? t(`paymentMethod.${rep.paymentMethod.toLowerCase()}`) : t('paymentMethod.unknown')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>

                      <Stack spacing={1} sx={{ width: '100%' }}>
                        <Stack direction="row" spacing={1} sx={{ width: '100%', flexWrap: 'wrap' }}>
                          <Button
                            variant="outlined"
                            size="medium"
                            onClick={() => openBookingDetails(rep)}
                            disabled={seriesBookings.some((b) => isBookingActionInProgress(b.roomBookingId))}
                            fullWidth
                            sx={{ flex: '1 1 120px' }}
                          >
                            {t('roomBookingRequests.viewBooking', 'View booking')}
                          </Button>
                          <Button
                            variant="contained"
                            color="success"
                            size="medium"
                            onClick={() => handleAcceptAllSeries(row.seriesId, seriesBookings)}
                            disabled={selectedSeries.length === 0 || isSeriesActionInProgress(row.seriesId)}
                            startIcon={
                              isSeriesActionLoading(row.seriesId, 'acceptAll') ? (
                                <CircularProgress size={16} color="inherit" />
                              ) : undefined
                            }
                            fullWidth
                            sx={{ flex: '1 1 120px' }}
                          >
                            {seriesAllChecked
                              ? t('roomBookingRequests.acceptAll', 'Accept all')
                              : t('roomBookingRequests.acceptSelected', 'Accept selected ({{count}})', {
                                  count: selectedSeries.length,
                                })}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            size="medium"
                            onClick={() => openDeclineDialog(seriesBookings)}
                            disabled={eligibleDecline.length === 0 || isSeriesActionInProgress(row.seriesId)}
                            fullWidth
                            sx={{ flex: '1 1 120px', borderColor: 'error.main', color: 'error.main' }}
                          >
                            {t('roomBookingRequests.declineAll', 'Decline all')}
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  </Card>
                );
              })}
            </Stack>
          </Box>

          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('roomBookingRequests.user')}</TableCell>
                    <TableCell>{t('roomBookingRequests.bookingDetails')}</TableCell>
                    <TableCell>{t('roomBookingRequests.additionalGears')}</TableCell>
                    <TableCell>{t('bookings.payment')}</TableCell>
                    <TableCell>{t('roomBookingRequests.status')}</TableCell>
                    <TableCell>{t('roomBookingRequests.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayRows.map((row, rowIndex) => (
                    <Fragment key={row.kind === 'single' ? row.booking.roomBookingId : row.seriesId}>
                      {row.kind === 'single' ? (
                        <>{renderDesktopSingleBookingFragment(row.booking)}</>
                      ) : (
                        <>
                          {(() => {
                            const seriesBookings = row.bookings;
                            const rep = seriesBookings[0];
                            const uniqueStatuses = Array.from(new Set(seriesBookings.map((b) => b.status)));
                            const showSeriesPartiallyConfirmed = seriesHasMixedConfirmedStates(seriesBookings);
                            const allSeriesSessionsPast = seriesBookings.every((b) => isPastBooking(b.start));
                            const selectedSeries = selectedSeriesBookings(row.seriesId, seriesBookings);
                            const seriesAllChecked =
                              selectedSeries.length === seriesBookings.length &&
                              !seriesBookings.some((booking) => seriesConflictIds.includes(booking.roomBookingId));
                            const seriesDisplayTotal = selectedSeries.reduce((sum, booking) => sum + booking.totalAmount, 0);
                            const eligibleDecline = seriesBookings.filter(canDeclineBooking);
                            const paymentStatuses = Array.from(
                              new Set(
                                seriesBookings
                                  .map((b) => b.paymentStatus)
                                  .filter((x): x is PaymentStatus => x != null)
                              )
                            );
                            const repBandName = resolveHostRoomBookingBandName(rep);
                            return (
                              <>
                      <TableRow>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <RoomBookingUserAvatar
                            userProfileImageUrl={rep.userProfileImageUrl}
                            displayName={rep.userName}
                            size={36}
                          />
                          <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                                      <Typography variant="body2">{rep.userName}</Typography>
                                      <Typography variant="caption" color="text.secondary">{rep.phoneNumber}</Typography>
                                      {repBandName ? <UserBandAffiliationLine bandName={repBandName} /> : null}
                                      <BookingCreatedAtLabel
                                        createdAt={rep.createdAt}
                                        timezone={rep.timezone}
                                        placement="inline"
                                        showTimezone={false}
                                      />
                        </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                      {allSeriesSessionsPast && renderPastChip(rep.start)}
                                      <Stack direction="row" flexWrap="wrap" gap={0.5} alignItems="center">
                                        <Chip
                                          icon={<RepeatIcon sx={{ fontSize: 16 }} />}
                                          label={t('roomBookingRequests.recurringBooking', 'Recurring')}
                                          color="secondary"
                                          variant="outlined"
                                          size="small"
                                        />
                                        <Chip
                                          label={t('roomBookingRequests.sessionsCount', '{{count}} sessions', {
                                            count: seriesBookings.length,
                                          })}
                                          size="small"
                                          variant="outlined"
                                        />
                                      </Stack>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                                          {t('bookingSteps.participantCount')}: {rep.participantDetails?.participantCount || '-'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                                          {t('roomBookingRequests.space')}: {rep.spaceName}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                          {t('roomBookingRequests.room')}: {rep.roomName}
                            </Typography>
                          </Box>
                          <Box>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                          {t('roomBookingRequests.sessionDates', 'Session dates')}
                            </Typography>
                                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                                          {renderSeriesSessionPicker(row.seriesId, seriesBookings)}
                                        </Stack>
                                        {renderSessionDateTapHint()}
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                          {t('roomBookingRequests.duration')}: {formatDuration(calculateDuration(rep.start, rep.end))}{' '}
                                          ({t('roomBookingRequests.perSession', 'per session')})
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                                  <TableCell>{formatAdditionalGears(rep.additionalGears)}</TableCell>
                      <TableCell>
                        <Stack spacing={0.5}>
                                      <Stack spacing={0.25}>
                                        <Typography variant="caption" color="text.secondary">
                                          {seriesAllChecked
                                            ? t('roomBookingRequests.totalForSessions', 'Total for {{count}} sessions', {
                                                count: seriesBookings.length,
                                              })
                                            : t(
                                                'roomBookingRequests.selectedSessionsCaption',
                                                '{{selected}} of {{total}} sessions',
                                                { selected: selectedSeries.length, total: seriesBookings.length },
                                              )}
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                          {formatPaymentAmount(seriesDisplayTotal, rep.currencyCode)}
                                        </Typography>
                                      </Stack>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        {getPaymentMethodIcon(rep.paymentMethod)}
                              <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                          {rep.paymentMethod ? t(`paymentMethod.${rep.paymentMethod.toLowerCase()}`) : t('paymentMethod.unknown')}
                              </Typography>
                          </Box>
                                                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                                        {paymentStatuses.length === 1 && !seriesBookings.some((b) => b.stripeRefundStatus) && (
                                <Chip 
                                            label={t(`paymentStatus.${String(paymentStatuses[0]).toLowerCase()}`)}
                                            color={getPaymentStatusColor(paymentStatuses[0] as string) as 'default'}
                                  size="small" 
                                />
                              )}
                                        {paymentStatuses.length > 1 && (
                                  <Chip 
                                            label={t('roomBookingRequests.mixedPayment', 'Mixed payment states')}
                                    size="small" 
                                            variant="outlined"
                                          />
                                        )}
                                        {seriesBookings.some((b) => b.stripeRefundStatus) && (
                                          <Chip
                                            icon={<ReceiptIcon fontSize="small" />}
                                            label={t('roomBookingRequests.refundInSeries', 'Refund on one or more sessions')}
                                            size="small"
                                            color="warning"
                                            variant="outlined"
                                          />
                              )}
                                        {seriesBookings.find((b) => b.pass) && (
                                          <PassBookingChip pass={seriesBookings.find((b) => b.pass)!.pass!} />
                                        )}
                            </Stack>
                        </Stack>
                      </TableCell>
                      <TableCell>
                                    <Stack direction="column" spacing={0.5}>
                                      {showSeriesPartiallyConfirmed ? (
                        <Chip 
                                          label={t('roomBookingRequests.partiallyConfirmedSeries', 'Partially confirmed')}
                                          color="warning"
                          size="small"
                          sx={{
                                            maxWidth: '100%',
                            '& .MuiChip-label': {
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                            },
                                          }}
                                        />
                                      ) : (
                                        uniqueStatuses.map((st) => (
                                          <Chip
                                            key={st}
                                            label={String(t(`status.${st.toLowerCase()}`, st))}
                                            color={statusColors[st as keyof typeof statusColors] || 'default'}
                                            size="small"
                                            sx={{
                                              maxWidth: '100%',
                                              '& .MuiChip-label': {
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              },
                                            }}
                                          />
                                        ))
                                      )}
                                    </Stack>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: 220 }}>
                          <Stack spacing={1}>
                            <Button
                              variant="contained"
                              color="success"
                              size="medium"
                              fullWidth
                              onClick={() => handleAcceptAllSeries(row.seriesId, seriesBookings)}
                              disabled={selectedSeries.length === 0 || isSeriesActionInProgress(row.seriesId)}
                              startIcon={
                                isSeriesActionLoading(row.seriesId, 'acceptAll') ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : undefined
                              }
                            >
                              {seriesAllChecked
                                ? t('roomBookingRequests.acceptAll', 'Accept all')
                                : t('roomBookingRequests.acceptSelected', 'Accept selected ({{count}})', {
                                    count: selectedSeries.length,
                                  })}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              size="medium"
                              fullWidth
                              onClick={() => openDeclineDialog(seriesBookings)}
                              disabled={eligibleDecline.length === 0 || isSeriesActionInProgress(row.seriesId)}
                              sx={{ borderColor: 'error.main', color: 'error.main' }}
                            >
                              {t('roomBookingRequests.declineAll', 'Decline all')}
                            </Button>
                            <Button
                              variant="outlined"
                              size="medium"
                              fullWidth
                              onClick={() => openBookingDetails(rep)}
                              disabled={seriesBookings.some((b) => isBookingActionInProgress(b.roomBookingId))}
                            >
                              {t('roomBookingRequests.viewBooking', 'View booking')}
                            </Button>
                          </Stack>
                        </Box>
                      </TableCell>
                    </TableRow>
                                {seriesBookings.map((b) =>
                                  isSessionBookingExpanded(b.roomBookingId)
                                    ? renderDesktopSingleBookingFragment(b, { nested: true })
                                    : renderChangeProposalRow(b)
                                )}
                              </>
                            );
                            })()}
                        </>
                    )}
                    {/* Separator row between bookings */}
                    {rowIndex < displayRows.length - 1 && (
                      <TableRow>
                        <TableCell 
                          colSpan={6} 
                          sx={{ 
                            height: 8,
                            p: 0,
                            borderTop: '1px solid',
                            borderBottom: 'none',
                            borderColor: 'divider',
                            bgcolor: 'transparent'
                          }}
                        />
                      </TableRow>
                    )}
                  </Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <TablePagination
            rowsPerPageOptions={isMobile ? [] : [5, 10, 25]}
            component="div"
            count={totalElements}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('common.pagination.rowsPerPage')}
            labelDisplayedRows={({ from, to, count }) => 
              t('common.pagination.displayedRows', { from, to, count })
            }
            sx={{
              '& .MuiTablePagination-selectLabel': {
                display: isMobile ? 'none' : 'block',
              },
              '& .MuiTablePagination-select': {
                display: isMobile ? 'none' : 'block',
              },
              '& .MuiTablePagination-selectIcon': {
                display: isMobile ? 'none' : 'block',
              },
            }}
          />
        </Box>
      )}

      <HostRecurringPartialAcceptDialog
        open={partialAccept != null}
        charged={(partialAccept?.charged ?? []).map((booking) => ({
          id: booking.roomBookingId,
          label: formatDateTime(booking.start),
        }))}
        declined={(partialAccept?.declined ?? []).map((booking) => ({
          id: booking.roomBookingId,
          label: formatDateTime(booking.start),
        }))}
        chargedTotalLabel={formatPaymentAmount(
          (partialAccept?.charged ?? []).reduce((sum, booking) => sum + booking.totalAmount, 0),
          partialAccept?.charged[0]?.currencyCode || 'PLN',
        )}
        reason={partialAcceptReason}
        loading={partialAccept != null && isSeriesActionInProgress(partialAccept.seriesId)}
        onReasonChange={setPartialAcceptReason}
        onClose={() => {
          if (partialAccept && isSeriesActionInProgress(partialAccept.seriesId)) return;
          setPartialAccept(null);
          setPartialAcceptReason('');
        }}
        onConfirm={() => {
          if (!partialAccept || !partialAcceptReason.trim()) return;
          void executeSeriesAccept(
            partialAccept.seriesId,
            [...partialAccept.charged, ...partialAccept.declined],
            partialAcceptReason.trim(),
          );
        }}
      />
      <BookingRejectionDialog
        open={declineDialogOpen}
        title={
          hostNegativeDialogMode === 'decline'
            ? declineTargets.length > 1
              ? t('roomBookingRequests.declineSeriesTitle', 'Decline recurring booking')
              : t('roomBookingRequests.declineTitle')
            : declineTargets.length > 1
              ? t('roomBookingRequests.cancelSeriesTitle', 'Cancel confirmed sessions')
              : t('roomBookingRequests.cancelTitle', 'Cancel confirmed booking')
        }
        description={
          hostNegativeDialogMode === 'decline'
            ? declineTargets.length > 1
              ? t(
                  'roomBookingRequests.declineSeriesDescription',
                  'Declining closes these requests. The person booking will be notified and will see the reason you provide.',
                )
              : t(
                  'roomBookingRequests.declineDescription',
                  'Declining closes this request. The person booking will be notified and will see the reason you provide.',
                )
            : declineTargets.length > 1
              ? t(
                  'roomBookingRequests.cancelAlreadyApprovedSurePlural',
                  'You have already approved these sessions. Are you sure you want to cancel them?',
                )
              : t(
                  'roomBookingRequests.cancelAlreadyApprovedSure',
                  'You have already approved this booking. Are you sure you want to cancel it?',
                )
        }
        reason={declineReason}
        reasonLabel={t('roomBookingRequests.declineReason')}
        reasonPlaceholder={t(
          hostNegativeDialogMode === 'decline'
            ? 'roomBookingRequests.declineReasonPlaceholder'
            : 'roomBookingRequests.cancelReasonPlaceholder',
          hostNegativeDialogMode === 'decline'
            ? 'Briefly explain why you cannot accept this request'
            : 'Briefly explain why you need to cancel this booking',
        )}
        reasonHelperText={t(
          hostNegativeDialogMode === 'decline'
            ? 'roomBookingRequests.declineReasonHelper'
            : 'roomBookingRequests.cancelReasonHelper',
          'A reason is required and will be shared with the person booking.',
        )}
        confirmLabel={
          hostNegativeDialogMode === 'decline'
            ? declineTargets.length > 1
              ? t('roomBookingRequests.declineAll', 'Decline all')
              : t('roomBookingRequests.decline')
            : t('roomBookingRequests.cancelBookingButton', 'Cancel booking')
        }
        cancelLabel={
          hostNegativeDialogMode === 'decline'
            ? t('common.cancel')
            : t('roomBookingRequests.cancelDialogClose', 'Close')
        }
        closeLabel={t('common.close', 'Close')}
        loading={isHostNegativeDialogLoading()}
        onReasonChange={setDeclineReason}
        onClose={closeHostNegativeDialog}
        onConfirm={handleHostNegativeDialogSubmit}
        confirmColor={hostNegativeDialogMode === 'decline' ? 'error' : 'warning'}
        fullScreen={isMobile}
        notice={
          hostNegativeDialogMode === 'decline' && declineTargetsUseSeriesEndpoint(declineTargets) ? (
            <Alert severity="warning">{t('bookings.declineSeriesWarning')}</Alert>
          ) : hostNegativeDialogMode === 'cancel' && declineTargets.length > 1 ? (
            <Alert severity="warning">
              {t(
                'roomBookingRequests.cancelSeriesWarning',
                'The guest may be notified according to your cancellation policy.',
              )}
            </Alert>
          ) : undefined
        }
      />

      <Dialog open={refundDialogOpen} onClose={closeRefundDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('roomBookingRequests.refundDialogTitle', 'Issue refund')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t(
                'roomBookingRequests.refundDialogDescription',
                'This will refund the online payment for this booking through Stripe. The refund may take a short time to appear.',
              )}
            </Typography>
            <TextField
              label={t('roomBookingRequests.refundReasonLabel', 'Refund note (optional)')}
              placeholder={t('roomBookingRequests.refundReasonPlaceholder', 'e.g. duplicate charge')}
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              fullWidth
              multiline
              minRows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRefundDialog} disabled={isBookingActionLoading(selectedBooking?.roomBookingId, 'refund')}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleRefundSubmit}
            color="warning"
            variant="contained"
            disabled={isBookingActionLoading(selectedBooking?.roomBookingId, 'refund')}
            startIcon={
              isBookingActionLoading(selectedBooking?.roomBookingId, 'refund') ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {t('roomBookingRequests.issueRefund', 'Issue refund')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={collectPaymentDialogOpen} onClose={closeCollectPaymentDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('roomBookingRequests.collectPaymentDialogTitle', 'Mark as paid')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
            {t(
              'roomBookingRequests.collectPaymentDialogDescription',
              'Confirm that cash payment was collected for this booking. The booking will be marked paid and the collection time will be recorded.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeCollectPaymentDialog}
            disabled={isBookingActionLoading(selectedBooking?.roomBookingId, 'collect')}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleCollectPaymentSubmit}
            color="success"
            variant="contained"
            disabled={isBookingActionLoading(selectedBooking?.roomBookingId, 'collect')}
            startIcon={
              isBookingActionLoading(selectedBooking?.roomBookingId, 'collect') ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            {t('roomBookingRequests.collectPayment', 'Mark as paid')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={acceptConflictDialogOpen && Boolean(getAcceptConflictBooking())}
        onClose={closeAcceptConflictDialog}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: 3,
            p: { xs: 1, sm: 1.5 },
            minHeight: { xs: 280, sm: 320 },
            maxWidth: { xs: 'calc(100% - 24px)', sm: 640 },
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                bgcolor: (theme) => theme.palette.warning.main,
                color: (theme) => theme.palette.warning.contrastText,
                flexShrink: 0,
              }}
            >
              <WarningAmberRoundedIcon fontSize="small" />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {t('roomBookingRequests.conflictDialogTitle', 'Booking conflict')}
            </Typography>
          </Stack>
          <IconButton
            aria-label={t('common.close', 'Close')}
            onClick={closeAcceptConflictDialog}
            sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, lineHeight: 1.45 }}>
            {t(
              'roomBookingRequests.conflictOnAccept',
              "You cannot approve this request as there's a conflicting booking for that time already."
            )}
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          <Button
            variant="outlined"
            color="primary"
            onClick={handleAcceptConflictPropose}
            size="medium"
          >
            {t('roomBookingRequests.proposeChange', 'Propose Change')}
          </Button>
          <Button
            variant="outlined"
            color="error"
            onClick={handleAcceptConflictReject}
            size="medium"
            sx={{ borderColor: 'error.main', color: 'error.main' }}
          >
            {t('roomBookingRequests.reject', 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Propose Change Dialog */}
      <Dialog 
        open={proposeChangeDialogOpen} 
        onClose={handleCloseProposeChangeDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: { xs: 0, sm: 3 },
            margin: { xs: 0, sm: 2 },
            maxWidth: { xs: '100%', sm: '900px' },
            maxHeight: { xs: '100vh', sm: '90vh' },
            boxShadow: theme.palette.mode === 'dark' 
              ? '0px 8px 32px rgba(0, 0, 0, 0.6)' 
              : '0px 4px 20px rgba(0, 0, 0, 0.12)',
            background: theme.palette.mode === 'dark' ? '#1F2129' : '#FFFFFF',
            border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
          }
        }}
      >
        {/* Header */}
        <Box sx={{ 
          p: { xs: 1.5, md: 2 }, 
          borderBottom: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
          bgcolor: theme.palette.mode === 'dark' ? '#1F2129' : '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700, 
                minWidth: 0,
                color: theme.palette.mode === 'dark' ? '#ECECFB' : '#212529',
                letterSpacing: '-0.02em'
              }}
              noWrap
              title={selectedBooking?.spaceName}
            >
              {selectedBooking?.spaceName}
            </Typography>
            {selectedBooking && (
              <Typography 
                variant="body2" 
                sx={{ color: 'text.secondary', minWidth: 0 }}
                noWrap
                title={selectedBooking.roomName}
              >
                • {selectedBooking.roomName}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="overline" sx={{ color: 'grey.400', letterSpacing: 2, display: { xs: 'none', sm: 'block' } }}>
              {t('roomBookingRequests.proposeChange', 'PROPOSE CHANGE')}
            </Typography>
            <IconButton aria-label="close" onClick={handleCloseProposeChangeDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <DialogContent sx={{ 
          pt: 3, 
          px: 3, 
          pb: 2,
          bgcolor: theme.palette.mode === 'dark' ? '#1F2129' : '#FFFFFF',
          overflowY: 'auto'
        }}>
          {loadingRooms ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              {/* Room Selector */}
              {availableRooms.length > 1 && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Current Room */}
                    <Box sx={{ flex: 1 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('roomBookingRequests.currentRoom', 'Current Room')}</InputLabel>
                        <Select
                          value={originalRoomId}
                          label={t('roomBookingRequests.currentRoom', 'Current Room')}
                          disabled
                        >
                          {availableRooms.map((room) => (
                            <MenuItem key={room.id} value={room.id}>
                              {room.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                    
                    {/* Arrow Icon */}
                    <ArrowForwardIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
                    
                    {/* New Room Dropdown */}
                    <Box sx={{ flex: 1 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel>{t('roomBookingRequests.selectRoom', 'New Room')}</InputLabel>
                        <Select
                          value={selectedRoomId}
                          label={t('roomBookingRequests.selectRoom', 'New Room')}
                          onChange={(e) => handleRoomChange(e.target.value)}
                        >
                          {availableRooms.map((room) => (
                            <MenuItem key={room.id} value={room.id}>
                              {room.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Time and Date Step */}
              {selectedRoomSummary && selectedDateTime && (
                <TimeAndDateStep
                  room={selectedRoomSummary}
                  bookingSlotsConfig={organizationBookingSlotsConfig}
                  initialDateTime={selectedDateTime}
                  initialDuration={selectedDuration}
                  showRecurringControls={false}
                  onValidityChange={setIsStepValid}
                  onBookingConfirmed={(booking) => {
                    setSelectedDateTime(new Date(booking.time));
                    setSelectedDuration(booking.duration);
                  }}
                />
              )}

              {/* Auto Accept Checkbox */}
              <Box sx={{ mt: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoAcceptRoomBooking}
                      onChange={(e) => setAutoAcceptRoomBooking(e.target.checked)}
                      sx={{
                        color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : 'rgba(0, 0, 0, 0.6)',
                        '&.Mui-checked': {
                          color: '#4E67EB',
                        },
                      }}
                    />
                  }
                  label={t('roomBookingRequests.autoAcceptRoomBooking', 'Automatically approve booking if change is accepted')}
                  sx={{
                    '& .MuiFormControlLabel-label': {
                      color: theme.palette.mode === 'dark' ? '#ECECFB' : '#212529',
                      fontSize: '0.875rem',
                    },
                  }}
                />
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ 
          p: 3, 
          borderTop: '1px solid',
          borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
          bgcolor: theme.palette.mode === 'dark' ? '#1F2129' : '#FFFFFF',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 2
        }}>
          <Button 
            onClick={handleCloseProposeChangeDialog}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              textTransform: 'uppercase',
              fontWeight: 600,
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleProposeChangeSubmit} 
            variant="contained" 
            disabled={!isStepValid || proposingChange || loadingRooms || !selectedRoomSummary || !hasProposeChangeDiff}
            startIcon={proposingChange ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{ 
              borderRadius: 2,
              textTransform: 'uppercase',
              fontWeight: 600,
              backgroundColor: '#4E67EB',
              color: '#ffffff',
              boxShadow: '0 2px 8px rgba(78, 103, 235, 0.25)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#3d56d9',
                boxShadow: '0 4px 12px rgba(78, 103, 235, 0.35)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&.Mui-disabled': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
              }
            }}
          >
            {proposingChange ? t('common.processing', 'Processing...') : t('common.confirm', 'Confirm')}
          </Button>
        </Box>
      </Dialog>
      </Card>
    </Box>
  );
}
